// Helpers 
export type NullableString = string | null
export type NullableNumber = number | null

export type LogTypeGroup = {
    category: NullableString
    log_types: (NullableString)[]
}

export type LicenseType = {
    active: boolean,
    warning: boolean,
    expire_date?: string | null,
    warning_message?: string | null
    is_expire?: boolean,
    warning_title?: string | null,
    renew_link_url?: string | null,
    renew_link_text?: string | null
}

export type RoomLoadPolicyMode = 'auto' | 'filter-required'

export type systemParamsType = {
    TAX: {
        VAT_RATE: NullableString
        ISSUE_TAX: boolean
        FORCE_ISSUE_TAX: boolean
        VAT_ISSUE_ON_PAYMENT: boolean
        VAT_ITEM_DESCRIPTION: NullableString
    }

    FOLIO: {
        FOLIO_FORMAT: NullableNumber
        FOLIO_OPTION: NullableNumber
        FOLIO_EDITABLE: boolean
        FOLIO_DEL_TRANS: boolean
        FOLIO_GEN_TRANS: boolean
        FOLIO_GRP_TRANS: boolean
        FOLIO_INS_TRANS: boolean
        FOLIO_MRG_TRANS: boolean
        FOLIO_EDIT_HEADER: boolean
        FOLIO_EDIT_ROOMNO: boolean
        FOLIO_PRINT_EMPTY: boolean
        FOLIO_EDIT_ARR_DEP: boolean
        FOLIO_EDIT_BALANCE: boolean
    }

    BACKUP: {
        BACKUPDAY?: NullableNumber
        BACKUP_DAYS?: NullableString
        CLEARING_DAYS: NullableString
        DBBACKUP_SERVER: NullableString
        DBBACKUP_DAYKEEP: NullableString
        DBBACKUP_DATABASE: NullableString
    }

    ENDDAY: {
        CHECK_AUTOPRINT: boolean
        ENDDAY_SKIP_PRINT: boolean
        ENDDAY_SKIP_BACKUP: boolean
        ENDDAY_CHECK_DEPOSIT: boolean
    }

    SYSTEM: {
        SHIFTS: (any)[]
        LANGUAGE: NullableString
        TIMEZONE: NullableString
        NAME_FORMAT: NullableString
        ONLINE_POST: boolean
        SYSTEM_DATE?: any// ISO date string
        ONLINE_SHIFT: NullableString
        AUTOPRINTFLAG: boolean
        ALLOW_OVERBOOK: boolean
        ENDDAY_RUNNING: boolean
        ALLOW_JOIN_ROOM: boolean
        NAME_FORCE_UPPER: boolean
        AUTO_CONFIRM_BOOKING: boolean
        AUTO_BLOCK_ROOM: boolean
        TAX_INVOICE_MODE?: 'ALL' | 'ON_DEMAND' // Tax invoice policy mode: ALL (ออกทุกใบ) or ON_DEMAND (ออกตามความต้องการ)
    }

    CASHIER: {
        CREDIT_CARD_MASK: NullableString
        CLOSE_SHIFT_BY_USER: boolean
    }

    DEFAULT: {
        VIP: number
        TITLE: NullableNumber
        ORIGIN: number
        ADULT_QTY: NullableNumber
        CHILD_QTY: NullableString
        ITEMIZERS: (NullableString)[]
        THAINATION: NullableString
        TITLE_MALE: NullableNumber
        TRXDEPOSIT: boolean
        APPROVERATE: boolean
        MSG_DAYKEEP: NullableNumber
        NATIONALITY: NullableNumber
        ACCOM_FORMAT: NullableString
        CHECKIN_TIME: NullableString  // "HH:mm"
        CREDIT_LIMIT: NullableString
        TITLE_FEMALE: NullableNumber
        CHECKOUT_TIME: NullableString // "HH:mm"
        CONTRACT_NOTE: boolean
        FOLIO_PATTERN: NullableNumber
        CONFIRM_LETTER: NullableString
        PAYMENT_METHOD: NullableString
        ABF_COMPBY_FLAG: boolean
        ALLOTBYROOMTYPE: boolean
        WALKIN_RATEPLAN: NullableNumber
        RESERVATION_TYPE: NullableNumber
        ROOM_CHARGE_DEPT: NullableNumber
    }

    DEPOSIT: {
        OPEN_FOLIO: boolean
        CANCEL_DEPT_ID: NullableNumber
        NOSHOW_DEPT_ID: NullableNumber
        DPS_NOSHOW_CODE: NullableString
        DPS_REFUND_DEPT: NullableString
        DPS_RECEIPT_CODE: NullableString
        DPS_REFUND_MEDIA: NullableString
        DPS_CLEARING_CODE: NullableString
        REFUND_PAYMENT_ID: NullableNumber
        DEFAULT_PAYMENT_ID?: any
        DPS_RECEIPTNO_AUTO: boolean
    }

    CALENDAR: {
        WEEKEND_DAYS: Record<'sun' | 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat', boolean>
        FIRST_MONTH_FISCAL: NullableString
        FIRST_MONTH_TOURISM: NullableString
    }

    AUTO_POST: {
        ABF: NullableNumber
        ROOM: NullableNumber
        LUNCH: NullableNumber
        DINNER: NullableNumber
        EXTRABED?: NullableNumber
    }

    LOG_TYPES: LogTypeGroup[]

    ROOM_RATE: {
        TAX_RATE: NullableNumber
        EXTRA_ADULT: NullableString  // เป็นตัวเลขในรูป string ในตัวอย่าง
        EXTRA_CHILD: NullableString  // เช่น "200"
        EXTRA_INFANT?: NullableNumber
        SERVICE_RATE: NullableNumber
        INCLUDE_ADULT: NullableNumber
        TAX_ON_SERVICE: boolean
        WEEKLY_RATE_MODE: NullableNumber
        MONTHLY_RATE_MODE: NullableNumber
        WEEKLY_RATE_ENABLED: boolean
        MONTHLY_RATE_ENABLED: boolean
        RATE_ROUND_MODE?: NullableNumber   // 0=none, 1=nearest, 2=round up, 3=round down
        RATE_ROUND_DIGITS?: NullableNumber
    }

    interfaces: {
        RR4: boolean
        NBIS: boolean
        TM30: boolean
        IDCARD: boolean
        KEYCARD: boolean
        PASSPORT: boolean
        SALESTAX: boolean
    }

    RESERVATION: {
        ALLOW_OVERBOOK: boolean
        ALLOTMENT_ENABLED: boolean
        AUTO_CONFIRM_BOOKING: boolean
    }

    ROOM_PLAN: {
        LOAD_POLICY?: RoomLoadPolicyMode
        TAB_TYPE?: 'room_group' | 'room_type' | 'bed_type' | 'building' | 'location' | 'exposure' | null
        TAB_DEFAULT_INDEX?: number | string | null
    }

    ROOM_STATUS: {
        LOAD_POLICY?: RoomLoadPolicyMode
        COLORS: {
            CL: string
            DI: string
            IS: string
            MI: string
            PU: string
            BLK: string
            HSU: string
            OCC: string
            OOI: string
            OOO: string
            OOS: string
            VAC: string
        }
        SELLABLE: Array<any>
        IS_ENABLED: boolean
        MI_ENABLED: boolean
        PU_ENABLED: boolean
        OOI_ENABLED: boolean
        OOS_ENABLED: boolean
        RETURN_STATUS: NullableString
        PICKUP_ENABLED: boolean
        CHECKOUT_STATUS: NullableString
        CLEAN_IS_SELLABLE: boolean
        INSPECTED_ENABLED: boolean
        INSPECTED_IS_SELLABLE: boolean
    }
    license_info: LicenseType

    DASHBOARD_URL: NullableString

    GUEST_PROFILE: {
        NAME_FORMAT: NullableString
        NAME_AUTO_MATCH: boolean
        NAME_FORCE_UPPER: boolean
    }

    // ถ้าต้องการระบุคีย์เฉพาะก็พิมพ์เป็น {'guest-welcome': NullableString}
    MESSAGE_TEMPLATE: Record<string, NullableString>

    LOCAL_SERVICE_DOWNLOAD_URL: NullableString
}

export const systemParamsDefault: systemParamsType = {
    TAX: {
        VAT_RATE: "7",
        ISSUE_TAX: false,
        FORCE_ISSUE_TAX: true,
        VAT_ISSUE_ON_PAYMENT: true,
        VAT_ITEM_DESCRIPTION: "Accomodations"
    },
    FOLIO: {
        FOLIO_FORMAT: 6,
        FOLIO_OPTION: null,
        FOLIO_EDITABLE: true,
        FOLIO_DEL_TRANS: true,
        FOLIO_GEN_TRANS: true,
        FOLIO_GRP_TRANS: true,
        FOLIO_INS_TRANS: true,
        FOLIO_MRG_TRANS: true,
        FOLIO_EDIT_HEADER: true,
        FOLIO_EDIT_ROOMNO: false,
        FOLIO_PRINT_EMPTY: false,
        FOLIO_EDIT_ARR_DEP: false,
        FOLIO_EDIT_BALANCE: false
    },
    BACKUP: {
        BACKUPDAY: 90,
        BACKUP_DAYS: "2",
        CLEARING_DAYS: "2",
        DBBACKUP_SERVER: "aa",
        DBBACKUP_DAYKEEP: "365",
        DBBACKUP_DATABASE: ""
    },
    ENDDAY: {
        CHECK_AUTOPRINT: true,
        ENDDAY_SKIP_PRINT: false,
        ENDDAY_SKIP_BACKUP: false,
        ENDDAY_CHECK_DEPOSIT: true
    },
    SYSTEM: {
        SHIFTS: ["A", "B", "C", "D"],
        LANGUAGE: "TH",
        TIMEZONE: "Asia/Bangkok",
        NAME_FORMAT: "LFT",
        ONLINE_POST: false,
        SYSTEM_DATE: undefined,
        ONLINE_SHIFT: "A",
        AUTOPRINTFLAG: true,
        ALLOW_OVERBOOK: true,
        ENDDAY_RUNNING: false,
        ALLOW_JOIN_ROOM: false,
        NAME_FORCE_UPPER: false,
        AUTO_CONFIRM_BOOKING: false,
        AUTO_BLOCK_ROOM: false,
        TAX_INVOICE_MODE: 'ALL' // Default: ALL mode (ออกทุกใบ)
    },
    CASHIER: {
        CREDIT_CARD_MASK: "NNNNNN******NNNN",
        CLOSE_SHIFT_BY_USER: false
    },
    DEFAULT: {
        VIP: 10,
        TITLE: 5,
        ORIGIN: 11,
        ADULT_QTY: 2,
        CHILD_QTY: "0",
        ITEMIZERS: ["SER", "VAT"],
        THAINATION: "T03",
        TITLE_MALE: 1,
        TRXDEPOSIT: true,
        APPROVERATE: true,
        MSG_DAYKEEP: 30,
        NATIONALITY: 1,
        ACCOM_FORMAT: "0",
        CHECKIN_TIME: "22:00",
        CREDIT_LIMIT: "4000",
        TITLE_FEMALE: 2,
        CHECKOUT_TIME: "23:00",
        CONTRACT_NOTE: false,
        FOLIO_PATTERN: 1,
        CONFIRM_LETTER: "1",
        PAYMENT_METHOD: "COA",
        ABF_COMPBY_FLAG: false,
        ALLOTBYROOMTYPE: true,
        WALKIN_RATEPLAN: 2,
        RESERVATION_TYPE: 16,
        ROOM_CHARGE_DEPT: 1,

    },
    DEPOSIT: {
        OPEN_FOLIO: true,
        CANCEL_DEPT_ID: 102,
        NOSHOW_DEPT_ID: 102,
        DPS_NOSHOW_CODE: "103",
        DPS_REFUND_DEPT: "92",
        DPS_RECEIPT_CODE: "91",
        DPS_REFUND_MEDIA: "99",
        DPS_CLEARING_CODE: "97",
        REFUND_PAYMENT_ID: 53,
        DEFAULT_PAYMENT_ID: 40,
        DPS_RECEIPTNO_AUTO: true
    },
    CALENDAR: {
        WEEKEND_DAYS: {
            fri: true,
            mon: false,
            sat: true,
            sun: false,
            thu: false,
            tue: false,
            wed: false
        },
        FIRST_MONTH_FISCAL: "11",
        FIRST_MONTH_TOURISM: "10"
    },
    AUTO_POST: {
        ABF: 13,
        ROOM: 1,
        LUNCH: 14,
        DINNER: 1,
        EXTRABED: 11
    },
    LOG_TYPES: [
        {
            category: "booking",
            log_types: [
                "Amend",
                "Amend Booking",
                "Block",
                "Cancel Booking",
                "Cancel check-in",
                "Cancel checkout",
                "Change block room",
                "Change Status",
                "Check in",
                "Check out",
                "Confirm",
                "Early check out",
                "New Booking",
                "No-show",
                "Rebook",
                "Unblock",
                "Unconfirm"
            ]
        },
        {
            category: "registration",
            log_types: [
                "Cancel check-in",
                "Cancel checkout",
                "Change departure",
                "Change rate",
                "Change room",
                "Check in",
                "Check out",
                "Early check out",
                "Extend stay",
                "Update registration"
            ]
        },
        {
            category: "cashier",
            log_types: [
                "Change folio pattern",
                "Move",
                "Payment",
                "Post",
                "Reopen folio",
                "Split",
                "Test",
                "Transfer in",
                "Transfer out",
                "Update",
                "Void"
            ]
        },
        {
            category: "room",
            log_types: [
                "Add out of order",
                "Change bed type",
                "Change room status",
                "Delete out of order",
                "Remove Out Of Order",
                "Room status changed",
                "Update Out Of Order"
            ]
        },
        {
            category: "inventory",
            log_types: [
                "Bulk Update"
            ]
        },
        {
            category: "settings",
            log_types: [
                "Add rate plan",
                "Channel Add ",
                "Channel Delete",
                "Channel Update",
                "Set rate plan active",
                "Set rate plan inactive",
                "Update rate plan"
            ]
        }
    ],
    ROOM_RATE: {
        TAX_RATE: 7,
        EXTRA_ADULT: "400",
        EXTRA_CHILD: "200",
        EXTRA_INFANT: 0,
        SERVICE_RATE: 10,
        INCLUDE_ADULT: 2,
        TAX_ON_SERVICE: true,
        WEEKLY_RATE_MODE: 1,
        MONTHLY_RATE_MODE: 2,
        WEEKLY_RATE_ENABLED: true,
        MONTHLY_RATE_ENABLED: false,
        RATE_ROUND_MODE: 0,
        RATE_ROUND_DIGITS: 0
    },
    interfaces: {
        RR4: true,
        NBIS: true,
        TM30: true,
        IDCARD: true,
        KEYCARD: true,
        PASSPORT: false,
        SALESTAX: true
    },
    RESERVATION: {
        ALLOW_OVERBOOK: false,
        ALLOTMENT_ENABLED: true,
        AUTO_CONFIRM_BOOKING: false
    },
    ROOM_PLAN: {
        LOAD_POLICY: "auto",
        TAB_TYPE: null,
        TAB_DEFAULT_INDEX: null
    },
    ROOM_STATUS: {
        LOAD_POLICY: "auto",
        COLORS: {
            CL: "#038FDE",
            DI: "#C4C4C4",
            IS: "#24FF00",
            MI: "#722ED1",
            PU: "#FADB14",
            BLK: "#00920A",
            HSU: "#77CDFF",
            OCC: "#FADB14",
            OOI: "#535353",
            OOO: "#FF0000",
            OOS: "#FA8C16",
            VAC: "#038FDE"
        },
        SELLABLE: [
            "CL",
            "IS"
        ],
        IS_ENABLED: true,
        MI_ENABLED: true,
        PU_ENABLED: true,
        OOI_ENABLED: true,
        OOS_ENABLED: false,
        RETURN_STATUS: "CL",
        PICKUP_ENABLED: false,
        CHECKOUT_STATUS: "IS",
        CLEAN_IS_SELLABLE: false,
        INSPECTED_ENABLED: true,
        INSPECTED_IS_SELLABLE: false
    },
    license_info: {
        active: false,
        warning: false,
        expire_date: undefined,
        warning_message: undefined
    },
    DASHBOARD_URL: "https://grafana.fromas.cloud:3000/public-dashboards/bc884dfd6eb7444d978c5ddd8c37e0d7?kiosk=tv&refresh=1m&theme=light",
    GUEST_PROFILE: {
        NAME_FORMAT: "LFMT",
        NAME_AUTO_MATCH: false,
        NAME_FORCE_UPPER: true
    },
    MESSAGE_TEMPLATE: {
        "guest-welcome": "guest-welcome-aonestar"
    },
    LOCAL_SERVICE_DOWNLOAD_URL: "https://tacydgwrzngwxmpbepqt.supabase.co/storage/v1/object/public/Release/IPULocalService-Dev-1-9-68.exe"
}
